import type { Capability, CapabilityRegistry } from "@loumy/contracts";

// §3.2 / §3.5 — CapabilityRegistry (server). Deprecate statt löschen (D32);
// Versionen sind immutable (D44): dieselbe (id,version) darf NICHT mit abweichender
// Impl re-registriert werden. Code-Retention ist zeitlich begrenzt (D43) — das
// erzwingt der Runner/CI-Report, nicht die Registry; deprecatete Versionen bleiben
// hier abrufbar (Resume aktiver Jobs).

export class CapabilityConflictError extends Error {
  constructor(
    readonly id: string,
    readonly version: number,
  ) {
    super(`Capability '${id}@${version}' ist bereits registriert — Versionen sind immutable (D44).`);
    this.name = "CapabilityConflictError";
  }
}

/** In-Memory-Implementierung der CapabilityRegistry (eager, server-seitig). */
export class InMemoryCapabilityRegistry implements CapabilityRegistry {
  /** id → version → Capability */
  private readonly byId = new Map<string, Map<number, Capability>>();
  private readonly deprecated = new Set<string>();

  private key(id: string, version: number): string {
    return `${id}@${version}`;
  }

  register(c: Capability): void {
    let versions = this.byId.get(c.id);
    if (!versions) {
      versions = new Map();
      this.byId.set(c.id, versions);
    }
    const existing = versions.get(c.version);
    // Idempotenter Re-Register desselben Objekts ist erlaubt (Hot-Reload/Doppel-Import);
    // eine ANDERE Impl unter gleicher (id,version) verletzt die Immutabilität (D44).
    if (existing && existing !== c) throw new CapabilityConflictError(c.id, c.version);
    // Shallow-Freeze (Codex-P2): sperrt Re-Assignment von id/version/surface/run-Referenz
    // als Foot-gun-Schutz. Die harte Immutabilitäts-Garantie ist der CI-Hash-Check (D44).
    versions.set(c.version, existing ?? Object.freeze(c));
  }

  /**
   * get(id, version) → exakte Version (Haupt-Pfad: der Plan pinnt die Version, D44);
   * deprecatete Versionen bleiben so für Resume/Anzeige abrufbar (D32).
   * get(id) ohne Version → höchste NICHT-deprecatete Version; sind ALLE deprecatet,
   * → undefined (Codex-P2: neue unversionierte Auflösung darf nichts Deprecatetes wählen).
   */
  get(id: string, version?: number): Capability | undefined {
    const versions = this.byId.get(id);
    if (!versions) return undefined;
    if (version !== undefined) return versions.get(version);

    const active: number[] = [];
    for (const v of versions.keys()) if (!this.deprecated.has(this.key(id, v))) active.push(v);
    if (active.length === 0) return undefined;
    return versions.get(Math.max(...active));
  }

  /** Markiert (id,version) als deprecatet (D32) — bleibt abrufbar, fällt aus get(id) ohne Version. */
  deprecate(id: string, version: number): void {
    if (!this.byId.get(id)?.has(version))
      throw new Error(`Kann nicht deprecaten: '${id}@${version}' ist nicht registriert.`);
    this.deprecated.add(this.key(id, version));
  }

  isDeprecated(id: string, version: number): boolean {
    return this.deprecated.has(this.key(id, version));
  }

  /** Alle registrierten Capabilities (für CI-Hash-Check / Retention-Report, D43/D44). */
  list(): Capability[] {
    const out: Capability[] = [];
    for (const versions of this.byId.values()) for (const c of versions.values()) out.push(c);
    return out;
  }
}
